import type { EtiquetaSeparacao } from '@/componentes/separacao/EtiquetaPapel';

interface PosicaoDeColeta {
  localizador: number;
  endereco: string | null;
  corredor: string | null;
  moduloOrdem: number | null;
  disponivel: number;
}

interface SkuDaOnda {
  produto: number;
  descricao: string | null;
  ean: string | null;
  posicoes: PosicaoDeColeta[];
  disponivelNoCentro: number;
}

interface ItemDoPedido {
  televenda: number;
  produto: number;
  quantidade: number;
}

interface PedidoDaOnda {
  televenda: number;
  estado: string | null;
  tipoEntrega: string | null;
  pedidoInternet: string | null;
  pedidoMarketplace: string | null;
}

/**
 * As etiquetas da onda que está montada na tela, antes de emitir.
 *
 * Reproduz o que `GERAR_ESTIQUETAS_SEPARACOES_TELEVENDAS` faria com esta
 * seleção: **uma etiqueta por UNIDADE**, com a posição escolhida na ordem do
 * PBS (`BOX, PRIORIDADE, saldo, localizador`) e o disponível de cada posição
 * sendo consumido ao longo da onda inteira — duas unidades do mesmo SKU em
 * pedidos diferentes disputam o mesmo saldo, e é por isso que a alocação não
 * pode ser feita pedido a pedido isoladamente.
 *
 * Por que existe uma prévia em vez de só ler o ERP: porque a pergunta do
 * operador é "quantas etiquetas a MINHA onda vai gerar, e para onde elas
 * mandam" — e ele faz essa pergunta ANTES de emitir, que é quando ainda dá
 * para tirar um pedido. Depois de emitido, o estoque já está reservado.
 *
 * O que a prévia NÃO tem, e não pode inventar: o número da etiqueta. Ele é o
 * id gerado no INSERT, e o código de barras é derivado dele
 * (`'1' + ZEROS(id,12)`). Etiqueta de prévia sai sem código — um código
 * inventado seria bipado, não encontrado, e viraria divergência.
 */
export function preverEtiquetas({
  marcados,
  pedidos,
  itens,
  catalogo,
  centro,
}: {
  marcados: ReadonlySet<number>;
  pedidos: readonly PedidoDaOnda[];
  itens: readonly ItemDoPedido[];
  catalogo: readonly SkuDaOnda[];
  centro: number;
}): EtiquetaSeparacao[] {
  const porProduto = new Map(catalogo.map((c) => [c.produto, c]));
  const porTelevenda = new Map(pedidos.map((p) => [p.televenda, p]));

  /**
   * Onde cada unidade seguinte deste SKU sai.
   *
   * Anda pelas posições na ordem do ERP e só passa para a próxima quando a
   * atual esgota o disponível — é a "derrama" que a procedure faz ao emitir
   * uma etiqueta por peça.
   */
  const cursor = new Map<number, { indice: number; usadoAqui: number }>();
  const proximaPosicao = (produto: number): PosicaoDeColeta | null => {
    const sku = porProduto.get(produto);
    if (!sku) return null;
    const atual = cursor.get(produto) ?? { indice: 0, usadoAqui: 0 };
    while (atual.indice < sku.posicoes.length) {
      const pos = sku.posicoes[atual.indice];
      if (pos && atual.usadoAqui < pos.disponivel) {
        atual.usadoAqui += 1;
        cursor.set(produto, atual);
        return pos;
      }
      atual.indice += 1;
      atual.usadoAqui = 0;
    }
    cursor.set(produto, atual);
    return null; // o centro não cobre: a etiqueta sai sem endereço, como 'ND'
  };

  const daOnda = itens.filter((i) => marcados.has(i.televenda));

  // Ordem estável de processamento: pedido e, dentro dele, SKU — a mesma do
  // `ORDER BY A.PRODUTO` da procedure.
  const ordenados = [...daOnda].sort((a, b) => a.televenda - b.televenda || a.produto - b.produto);

  const unidadesPorPedido = new Map<number, number>();
  const unidadesPorPedidoProduto = new Map<string, number>();
  for (const i of ordenados) {
    unidadesPorPedido.set(i.televenda, (unidadesPorPedido.get(i.televenda) ?? 0) + i.quantidade);
    const chave = `${i.televenda}:${i.produto}`;
    unidadesPorPedidoProduto.set(chave, (unidadesPorPedidoProduto.get(chave) ?? 0) + i.quantidade);
  }

  const contaPedido = new Map<number, number>();
  const contaPedidoProduto = new Map<string, number>();
  const etiquetas: EtiquetaSeparacao[] = [];

  for (const item of ordenados) {
    const sku = porProduto.get(item.produto);
    const pedido = porTelevenda.get(item.televenda);
    const chave = `${item.televenda}:${item.produto}`;

    for (let n = 0; n < item.quantidade; n += 1) {
      const pos = proximaPosicao(item.produto);
      const noPedido = (contaPedido.get(item.televenda) ?? 0) + 1;
      const noProduto = (contaPedidoProduto.get(chave) ?? 0) + 1;
      contaPedido.set(item.televenda, noPedido);
      contaPedidoProduto.set(chave, noProduto);

      const ean = sku?.ean ?? null;
      etiquetas.push({
        // Sem id do ERP: a prévia não tem etiqueta gravada. Chave só para o React.
        etiqueta: -etiquetas.length - 1,
        codigoBarras: null,
        emissao: 0,
        televenda: item.televenda,
        centroEstoque: centro,
        sequencia: etiquetas.length + 1,
        volumes: null,
        volumeProdutos: `${noProduto}/${unidadesPorPedidoProduto.get(chave) ?? 1}`,
        volumePedido: `${noPedido}/${unidadesPorPedido.get(item.televenda) ?? 1}`,
        produto: item.produto,
        descricao: sku?.descricao ?? null,
        descricaoEtiqueta:
          `${item.produto} - ${sku?.descricao ?? ''}` + (ean ? `- EAN: ${ean.slice(-6)} ` : ''),
        quantidade: 1,
        localizador: pos?.localizador ?? null,
        endereco: pos?.endereco ?? null,
        corredor: pos?.corredor ?? null,
        status: null,
        lote: null,
        estado: pedido?.estado ?? null,
        transporte: pedido?.tipoEntrega ?? null,
        localFaturamento: `FAT - ${centro}`,
        presente: false,
        pedidoInternet: pedido?.pedidoInternet ?? null,
        pedidoMarketplace: pedido?.pedidoMarketplace ?? null,
      });
    }
  }

  /*
   * A ordem de IMPRESSÃO do relatório do PBS, que não é a de geração:
   * pedido com mais de uma etiqueta sai primeiro, com as peças juntas (senão
   * se perdem na esteira); depois os de etiqueta única, em ordem de ROTA —
   * corredor e módulo —, que é a caminhada do separador.
   */
  const naturais = (a: string, b: string): number =>
    a.length - b.length || a.localeCompare(b, 'pt-BR');

  return etiquetas
    .map((e, i) => ({ e, i }))
    .sort((x, y) => {
      const multiX = (unidadesPorPedido.get(x.e.televenda) ?? 1) > 1 ? 1 : 2;
      const multiY = (unidadesPorPedido.get(y.e.televenda) ?? 1) > 1 ? 1 : 2;
      if (multiX !== multiY) return multiX - multiY;
      if (multiX === 1 && x.e.televenda !== y.e.televenda) return x.e.televenda - y.e.televenda;
      const rua = naturais(x.e.corredor ?? '~', y.e.corredor ?? '~');
      if (rua !== 0) return rua;
      return x.e.televenda - y.e.televenda || x.i - y.i;
    })
    .map(({ e }, i) => ({ ...e, sequencia: i + 1 }));
}
